import { useEffect, useState } from "react";

export type RotatingHeadlineMessage = {
  /** Bold opening phrase (weight inherited from the surrounding heading). */
  lead: string;
  /** Normal-weight remainder, matching the solar savings heading treatment. */
  rest?: string;
};

type RotatingHeadlineProps = {
  messages: RotatingHeadlineMessage[];
  intervalMs?: number;
};

export function RotatingHeadline({ messages, intervalMs = 5000 }: RotatingHeadlineProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
      if (mediaQuery.matches) {
        setActiveIndex(0);
      }
    };

    updateMotionPreference();
    mediaQuery.addEventListener("change", updateMotionPreference);

    return () => mediaQuery.removeEventListener("change", updateMotionPreference);
  }, []);

  useEffect(() => {
    if (messages.length < 2 || isPaused || prefersReducedMotion) {
      return;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % messages.length);
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [intervalMs, isPaused, messages.length, prefersReducedMotion]);

  if (messages.length === 0) {
    return null;
  }

  return (
    <span
      className="relative grid"
      tabIndex={0}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      {messages.map((message, index) => (
        <span
          key={message.lead}
          style={{ gridArea: "1 / 1" }}
          className={`transition-opacity duration-[400ms] motion-reduce:transition-none ${
            index === activeIndex ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden={index === activeIndex ? undefined : true}
        >
          {message.lead}
          {message.rest && <span className="font-normal"> {message.rest}</span>}
        </span>
      ))}
    </span>
  );
}
